import React from 'react';
import LlcLogo from './LlcLogo';

function AboutPage({ C, useBreakpoint }) {
	const { isMobile, isTablet, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	const orgs = [
		'Greenwood Cultural Center, Tulsa',
		'John Hope Franklin Center for Reconciliation',
		'1921 Race Massacre Commission',
		'Tulsa Historical Society & Museum',
		'University researchers in historical demography',
		'Descendant organizations and family networks',
		'Archivists with access to church & burial records',
	];

	const CONTACT_EMAIL = 'thecenterfortheuncounted@censusdisruption.org';

	const roles = [
		{
			label: 'I am a descendant of Greenwood',
			subject: 'CDM Research, Descendant Connection',
			body: 'I have a family connection to the Greenwood community and would like to learn more about the Census Disruption Model research.',
		},
		{
			label: 'I am a researcher or historian',
			subject: 'CDM Research, Academic Collaboration',
			body: 'I am a researcher or historian interested in collaborating on or contributing to the Census Disruption Model project.',
		},
		{
			label: 'I represent a Tulsa institution',
			subject: 'CDM Research, Institutional Partnership',
			body: 'I represent a Tulsa institution and am interested in discussing a partnership with the Census Disruption Model research team.',
		},
		{
			label: 'I want to apply CDM to another event',
			subject: 'CDM Framework, New Application',
			body: 'I am interested in applying the Census Disruption Model framework to another historical event and would like to discuss this further.',
		},
	];

	return (
		<div style={{ padding: `60px ${px} 48px` }}>
			<div
				style={{
					paddingTop: 50,
				}}
			>
				<LlcLogo width={600} />
			</div>
			<div>
				<div className='section-label fade-up' style={{ marginBottom: 20 }}>
					About
				</div>
				<h1
					className='display-heading fade-up delay-1'
					style={{
						fontSize: compact ? 'clamp(28px,7vw,50px)' : 'clamp(36px,5vw,60px)',
						maxWidth: 680,
						marginBottom: 40,
					}}
				>
					Built in service of a community,
					<br />
					<span style={{ color: C.teal }}>not in place of it.</span>
				</h1>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
						gap: compact ? 40 : 72,
					}}
				>
					{/* About copy */}
					<div>
						{/* Updated: named the institution and Blake, grounded the origin story */}
						<p
							style={{
								fontSize: compact ? 14 : 16,
								color: '#8AACAA',
								lineHeight: 1.9,
								marginBottom: 18,
							}}
						>
							The Center for the Uncounted is a Tulsa-based research
							organization founded to identify individuals erased from the
							historical record by mass violence. The Census Disruption Model
							(CDM) is its first instrument, built by founder Blake McPherson, a
							researcher and technologist who does not claim authority over this
							history. The model is a tool. Its value is entirely contingent on
							whether it serves the communities whose ancestors it seeks to
							account for.
						</p>
						<p
							style={{
								fontSize: compact ? 14 : 16,
								color: '#6A8A86',
								lineHeight: 1.9,
								marginBottom: 32,
							}}
						>
							This project began with a simple question: why does one of the
							worst acts of racial violence in American history have almost no
							official death certificates? That question led to years of
							research, a probabilistic framework built on IPUMS full-count
							census microdata, and a risk pool of approximately 2,100
							individuals who disappear from the historical record exactly where
							and when you would expect, if the massacre happened to them.
						</p>

						<div
							style={{
								background: '#111E1C',
								border: '1px solid #1A2E2A',
								borderLeft: `3px solid ${C.teal}`,
								padding: '20px 24px',
								marginBottom: 50,
							}}
						>
							<div
								style={{
									fontFamily: 'DM Mono',
									fontSize: 9,
									letterSpacing: '0.15em',
									textTransform: 'uppercase',
									color: C.tealLight,
									marginBottom: 10,
								}}
							>
								Core Commitment
							</div>
							<p
								style={{
									fontFamily: 'Playfair Display',
									fontStyle: 'italic',
									fontSize: compact ? 15 : 17,
									color: 'white',
									lineHeight: 1.6,
								}}
							>
								"The data belongs to the community."
							</p>
						</div>
					</div>

					{/* Collaboration list */}
					<div>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								letterSpacing: '0.15em',
								textTransform: 'uppercase',
								color: C.tealLight,
								marginBottom: 18,
							}}
						>
							Seeking Collaboration With
						</div>
						{orgs.map((org, i) => (
							<div
								key={org}
								style={{
									padding: '13px 0',
									borderBottom: '1px solid #1A2E2A',
									fontSize: 14,
									color: i === 0 ? '#A0B0AE' : '#6A8A86',
									display: 'flex',
									gap: 12,
									alignItems: 'center',
									transition: 'color 0.2s',
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.color = C.tealLight)
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.color =
										i === 0 ? '#A0B0AE' : '#6A8A86')
								}
							>
								<span style={{ color: C.teal, fontSize: 7, flexShrink: 0 }}>
									◆
								</span>
								{org}
							</div>
						))}

						{/* Updated: institutional location and founding context */}
						<div
							style={{
								marginTop: 28,
								padding: '16px 20px',
								background: '#111E1C',
								border: '1px solid #1A2E2A',
							}}
						>
							<div
								style={{
									fontFamily: 'DM Mono',
									fontSize: 9,
									letterSpacing: '0.15em',
									textTransform: 'uppercase',
									color: C.tealLight,
									marginBottom: 8,
								}}
							>
								Based In
							</div>
							<div style={{ fontSize: 13, color: '#6A8A86', lineHeight: 1.7 }}>
								Tulsa, Oklahoma, the city where this history happened, and where
								this work belongs.
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* ── TIMELINE / ETHICS STRIP ── */}
			<div style={{ padding: `0 ${px} 60px` }}>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: isMobile ? '1fr' : 'repeat(4,1fr)',
						gap: 2,
					}}
				>
					{[
						{ n: 'No', d: 'redistributed data from IPUMS microdata' },
						{ n: 'No', d: 'victim status without corroboration' },
						{ n: 'Full', d: 'transparency in methodology' },
						{ n: 'Open', d: 'to correction and community override' },
					].map(({ n, d }) => (
						<div
							key={d}
							style={{
								background: '#111E1C',
								border: '1px solid #1A2E2A',
								padding: compact ? '20px 16px' : '28px 24px',
							}}
						>
							<div
								style={{
									fontFamily: 'Playfair Display',
									fontSize: compact ? 28 : 36,
									color: C.tealLight,
									fontWeight: 600,
									marginBottom: 8,
								}}
							>
								{n}
							</div>
							<div style={{ fontSize: 12, color: '#4A6A66', lineHeight: 1.6 }}>
								{d}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── GET INVOLVED ── */}
			<div
				style={{
					background: '#070F0D',
					padding: `56px ${px}`,
					display: 'grid',
					gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
					gap: compact ? 40 : 72,
					alignItems: 'center',
					borderTop: '1px solid #1A2E2A',
				}}
			>
				<div>
					<div
						style={{
							fontFamily: 'DM Mono',
							fontSize: 10,
							letterSpacing: '0.15em',
							textTransform: 'uppercase',
							color: C.tealLight,
							marginBottom: 16,
						}}
					>
						Get Involved
					</div>
					<h2
						style={{
							fontFamily: 'Playfair Display',
							color: 'white',
							fontSize: compact ? 26 : 34,
							marginBottom: 18,
							lineHeight: 1.2,
						}}
					>
						This work cannot be completed by a single researcher.
					</h2>
					<p style={{ color: '#6A8A86', fontSize: 15, lineHeight: 1.9 }}>
						If you have family connections to the Greenwood community, are a
						researcher engaged with the 1921 massacre, or are interested in
						applying this framework to other historical events, we want to hear
						from you.
					</p>
					{/* Updated: direct email visible as a fallback */}
					<p
						style={{
							marginTop: 16,
							fontSize: 12,
							color: '#2A4A46',
							fontFamily: 'DM Mono',
						}}
					>
						{CONTACT_EMAIL}
					</p>
				</div>

				<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{roles.map(({ label, subject, body }) => {
						const href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
						return (
							<a
								key={label}
								href={href}
								style={{
									background: '#111E1C',
									border: '1px solid #1A2E2A',
									padding: '16px 20px',
									display: 'flex',
									gap: 12,
									alignItems: 'center',
									cursor: 'pointer',
									fontSize: 14,
									color: '#6A8A86',
									transition: 'all 0.2s',
									textDecoration: 'none',
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.background = '#162220';
									e.currentTarget.style.borderColor = C.teal;
									e.currentTarget.style.color = '#A0B0AE';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.background = '#111E1C';
									e.currentTarget.style.borderColor = '#1A2E2A';
									e.currentTarget.style.color = '#6A8A86';
								}}
							>
								<span style={{ color: C.teal, fontSize: 7, flexShrink: 0 }}>
									◆
								</span>
								{label}
								<span
									style={{ marginLeft: 'auto', color: '#2A4A46', fontSize: 16 }}
								>
									→
								</span>
							</a>
						);
					})}
				</div>
			</div>
		</div>
	);
}

export default AboutPage;
